import React, { FC } from 'react';
import styled from 'styled-components';
import EditIcon from '@material-ui/icons/Edit';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import { InspectionStatuses } from '../../../../constants';

type StatusTone = 'success' | 'warning' | 'info' | 'neutral' | 'danger';

const tonePalette: Record<StatusTone, { bg: string; fg: string; border: string }> = {
  success: { bg: '#E8F6EE', fg: '#196A3B', border: '#C5E7D2' },
  warning: { bg: '#FFF6E0', fg: '#8A5F05', border: '#FFE7AC' },
  info: { bg: '#E8F2FD', fg: '#1E64BF', border: '#CFE6F3' },
  danger: { bg: '#FDECEC', fg: '#9A2523', border: '#F9C9C9' },
  neutral: { bg: '#F2F4F7', fg: '#3B424E', border: '#E6E9EF' },
};

const resolveTone = (status: number | string): StatusTone => {
  if (typeof status === 'string') {
    const s = status.toLowerCase();
    if (s === 'completed') return 'success';
    if (s === 'pending' || s === 'waiting') return 'neutral';
    if (s === InspectionStatuses.REJECTED.toLowerCase() || s === 'rejected' || s === 'failed') return 'danger';
    if (s === 'submitted' || s === 'under review') return 'info';
    return 'warning';
  }
  // Numeric status codes (from backend)
  const completed = [31, 42, 43, 167, 187, 182, 177];
  const rejected = [46, 165, 189, 184, 179];
  const pending = [45, 162, 176, 174, 175];
  const info = [47, 166, 191, 186, 181, 164, 190, 185, 180];
  if (completed.includes(status as number)) return 'success';
  if (rejected.includes(status as number)) return 'danger';
  if (pending.includes(status as number)) return 'neutral';
  if (info.includes(status as number)) return 'info';
  return 'warning';
};

const resolveStatusLabel = (status: number | string, statusName?: string): string => {
  if (statusName) return statusName;
  if (typeof status === 'string') return status;
  return '—';
};

type Props = { item: any; onEdit: Function };

const SingleItemWithDownload: FC<Props> = ({ item, onEdit }) => {
  const attachmentsCount = Array.isArray(item.attachments) ? item.attachments.length : 0;
  const tone = resolveTone(item.statusName || item.status);
  const palette = tonePalette[tone];
  const label = resolveStatusLabel(item.status, item.statusName);

  return (
    <Tile>
      <TopRow>
        <StatusChip style={{ background: palette.bg, color: palette.fg, borderColor: palette.border }}>{label}</StatusChip>
        <EditButton type="button" title="Edit condition" onClick={e => onEdit(e, item)}>
          <EditIcon />
        </EditButton>
      </TopRow>
      <ItemName title={item.name}>{item.name}</ItemName>
      <Footer>
        <AttachFileIcon style={{ fontSize: 14 }} />
        <span>
          {attachmentsCount} {attachmentsCount === 1 ? 'attachment' : 'attachments'}
        </span>
      </Footer>
    </Tile>
  );
};

export default SingleItemWithDownload;

const Tile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  min-width: 0;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;

  &:hover {
    border-color: var(--neutral-300);
    box-shadow: var(--shadow-sm, 0 2px 6px rgba(15, 23, 42, 0.08));
    transform: translateY(-1px);
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid var(--border);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1.4;
  white-space: nowrap;
  max-width: 70%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EditButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-500);
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  svg {
    font-size: 18px;
  }

  &:hover {
    background: var(--primary-50);
    border-color: var(--primary-200, #a1cde6);
    color: var(--primary-600);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const ItemName = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--mainText);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Footer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--neutral-500);
  font-size: 0.75rem;

  svg {
    color: var(--neutral-500);
  }
`;
